import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ExternalLink, KeyRound, Laptop, Loader2, Trash2, User } from "lucide-react";
import { AI_PROVIDERS, aiProviderValues, type AiKeyInfo, type AiProvider } from "@mycompbox/shared";
import {
  useAiBalance,
  useAiSettings,
  useDeleteAiKey,
  useSaveAiKey,
  useVerifyAiKey,
  type KeyOwner,
} from "@/hooks/queries/use-ai-settings";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { machineReadiness } from "@/lib/api/mailbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AiUsageSection } from "./ai-usage-section";

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

/**
 * Guided "add an API key" form: pick a provider, follow its steps, paste the
 * key, check it, pick a model, save. `onDone` runs after Cancel or a save;
 * `onSaved` runs only after a save.
 */
export function ApiKeyForm({
  owner,
  onDone,
  onSaved,
}: {
  owner: KeyOwner;
  onDone: () => void;
  onSaved?: () => void;
}) {
  const [provider, setProvider] = useState<AiProvider>("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [models, setModels] = useState<string[] | null>(null);
  const [model, setModel] = useState("");
  const verify = useVerifyAiKey();
  const save = useSaveAiKey(owner);
  const info = AI_PROVIDERS[provider];

  const trimmed = apiKey.trim();
  const prefixLooksWrong = trimmed.length > 6 && !trimmed.startsWith(info.keyPrefix);

  function resetCheck() {
    setModels(null);
    setModel("");
    verify.reset();
    save.reset();
  }

  async function handleCheck() {
    const result = await verify.mutateAsync({ provider, apiKey: trimmed });
    setModels(result.data.models);
    setModel(result.data.models[0] ?? "");
  }

  async function handleSave() {
    await save.mutateAsync({ provider, apiKey: trimmed, model });
    setApiKey("");
    onSaved?.();
    onDone();
  }

  const error = errorMessage(verify.error ?? save.error);

  return (
    <div className="space-y-4 rounded-lg border border-white/[0.08] p-4">
      <p className="text-sm font-medium">{owner === "company" ? "Company key for everyone" : "Your own key"}</p>
      <div className="space-y-2">
        <Label>Provider</Label>
        <div className="flex flex-wrap gap-2">
          {aiProviderValues.map((id) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={id === provider ? "default" : "outline"}
              onClick={() => {
                setProvider(id);
                resetCheck();
              }}
            >
              {AI_PROVIDERS[id].name}
            </Button>
          ))}
        </div>
        {info.note ? <p className="text-xs text-muted-foreground">{info.note}</p> : null}
      </div>

      <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
        {info.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3 text-sm">
        <a href={info.keyPageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
          Open the {info.name} key page <ExternalLink className="h-3 w-3" />
        </a>
        <a href={info.limitsPageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:underline">
          {info.reportsBalance ? "Add credits" : "Set a spending limit"} <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`ai-key-${owner}`}>Paste the API key</Label>
        <Input
          id={`ai-key-${owner}`}
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder={`${info.keyPrefix}…`}
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            resetCheck();
          }}
        />
        {prefixLooksWrong ? (
          <p className="text-xs text-amber-300">
            {info.name} keys usually start with “{info.keyPrefix}”. Check that you copied the right key.
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          CompBox checks the key with {info.name}, then stores it encrypted. Nobody, including you, can view it again; it
          can only be replaced or removed.
        </p>
      </div>

      {models ? (
        <div className="space-y-2">
          <Label htmlFor={`ai-model-${owner}`}>Model</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger id={`ai-model-${owner}`}>
              <SelectValue placeholder="Choose a model" />
            </SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-emerald-300">Key works. {models.length} models are available to it.</p>
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        {models ? (
          <Button type="button" onClick={() => void handleSave().catch(() => undefined)} disabled={!model || save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save key
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => void handleCheck().catch(() => undefined)}
            disabled={trimmed.length < 8 || verify.isPending}
          >
            {verify.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Check key
          </Button>
        )}
      </div>
    </div>
  );
}

function KeyRow({
  icon,
  label,
  info,
  inUse,
  emptyText,
  canEdit,
  storageAvailable,
  onEdit,
  owner,
}: {
  icon: React.ReactNode;
  label: string;
  info: AiKeyInfo | null;
  inUse: boolean;
  emptyText: string;
  canEdit: boolean;
  storageAvailable: boolean;
  onEdit: () => void;
  owner: KeyOwner;
}) {
  const remove = useDeleteAiKey(owner);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 text-sm font-medium">
          {icon}
          {label}
          {inUse ? (
            <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">
              In use
            </Badge>
          ) : null}
        </div>
        {info ? (
          <p className="truncate text-sm text-muted-foreground">
            {AI_PROVIDERS[info.provider].name} · <span className="font-mono">{info.model}</span> · key ending{" "}
            <span className="font-mono">…{info.last4}</span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        )}
        {errorMessage(remove.error) ? <p className="text-xs text-destructive">{errorMessage(remove.error)}</p> : null}
      </div>
      {canEdit ? (
        <div className="flex shrink-0 gap-2">
          {storageAvailable ? (
            <Button variant="outline" size="sm" onClick={onEdit}>
              {info ? "Replace" : "Add key"}
            </Button>
          ) : null}
          {info ? (
            <Button variant="outline" size="sm" onClick={() => { remove.mutate(); }} disabled={remove.isPending}>
              <Trash2 className="mr-1 h-3 w-3" />
              Remove
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Account-level AI settings: the person's paired computers, their own API key,
 * and the company key an org admin sets for everyone. Which one a piece of
 * work uses is chosen each time it is sent.
 */
export function AiSettingsCard() {
  const { data: settings, isLoading } = useAiSettings();
  const { data: machines } = useMachines();
  const [editing, setEditing] = useState<KeyOwner | null>(null);

  const effective =
    settings?.effectiveKey === "personal" ? settings.personalKey : settings?.effectiveKey === "company" ? settings.companyKey : null;
  const reportsBalance = effective ? AI_PROVIDERS[effective.provider].reportsBalance : false;
  const { data: balance } = useAiBalance(reportsBalance);

  if (isLoading || !settings) {
    return <p className="text-sm text-muted-foreground">Loading AI settings…</p>;
  }

  const paired = (machines ?? []).filter((m) => m.status !== "revoked");
  const ready = paired.filter((m) => machineReadiness(m) === "ready").length;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Each time you send work, you choose where it runs: on your paired computer, or with an API key. Set both up here.
      </p>

      <div className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Laptop className="h-4 w-4" />
            Your computer
          </div>
          <p className="text-sm text-muted-foreground">
            {paired.length === 0
              ? "No computer paired yet."
              : `${String(paired.length)} paired, ${String(ready)} ready. Work uses whatever AI account and model that computer is set to.`}
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="shrink-0">
          <Link to="/connect">{paired.length === 0 ? "Connect a computer" : "Connect another"}</Link>
        </Button>
      </div>

      <KeyRow
        icon={<Building2 className="h-4 w-4" />}
        label="Company key"
        info={settings.companyKey}
        inUse={settings.effectiveKey === "company"}
        emptyText={settings.canManageCompanyKey ? "No company key. Add one so everyone can use it without setup." : "Your admin hasn't added a company key."}
        canEdit={settings.canManageCompanyKey}
        storageAvailable={settings.keyStorageAvailable}
        onEdit={() => { setEditing("company"); }}
        owner="company"
      />

      <KeyRow
        icon={<User className="h-4 w-4" />}
        label="Your own key"
        info={settings.personalKey}
        inUse={settings.effectiveKey === "personal"}
        emptyText={settings.companyKey ? "None. You're using the company key." : "None."}
        canEdit
        storageAvailable={settings.keyStorageAvailable}
        onEdit={() => { setEditing("personal"); }}
        owner="personal"
      />

      {effective ? (
        <div className="flex items-center gap-2 text-sm">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          {reportsBalance && balance?.supported ? (
            <span>
              {balance.remainingUsd !== null
                ? `${formatUsd(balance.remainingUsd)} of ${AI_PROVIDERS[effective.provider].name} credit left`
                : `${AI_PROVIDERS[effective.provider].name} credit balance unknown`}
              {balance.usedUsd !== null ? ` · ${formatUsd(balance.usedUsd)} used on this key` : ""}
              {balance.freeTier ? " · free models only" : ""}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {AI_PROVIDERS[effective.provider].name} doesn't share your remaining balance with CompBox.{" "}
              <a href={AI_PROVIDERS[effective.provider].limitsPageUrl} target="_blank" rel="noreferrer" className="underline">
                Check it on their site
              </a>
              .
            </span>
          )}
        </div>
      ) : null}

      {!settings.keyStorageAvailable ? (
        <p className="text-xs text-amber-300">Key storage isn't set up on this server yet, so keys can't be saved.</p>
      ) : null}
      {!settings.cloudRunnerAvailable ? (
        <p className="text-xs text-muted-foreground">
          Request interviews run on your API key now. Planning and building on a key is coming soon; until then they run on
          your computer.
        </p>
      ) : null}

      {effective ? <AiUsageSection /> : null}

      {editing ? <ApiKeyForm key={editing} owner={editing} onDone={() => { setEditing(null); }} /> : null}
    </div>
  );
}
